import React, { useMemo, useRef, useState } from 'react';
import { fmtMoney, fmtPct, signClass } from '../../utils/format';
import './PriceChart.css';

// Ink-style price chart drawn from the raw series the backend returns.
// Daily bars are evenly spaced (trading days, like every broker chart).
// Optional layers: the S&P 500 rebased to the first close, and the reader's
// own average cost as a bronze rule. Hover or touch to read a bar.

function niceTicks(min, max, count) {
  const span = max - min || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
  const start = Math.ceil(min / step) * step;
  const ticks = [];
  for (let v = start; v <= max + 1e-9; v += step) ticks.push(Number(v.toFixed(6)));
  return ticks;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function labelFor(dateStr, spanDays) {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (spanDays > 400) return `${MONTHS[m - 1]} ${String(y).slice(2)}`;
  if (spanDays > 60) return `${MONTHS[m - 1]} ${String(y).slice(2)}`;
  return `${d} ${MONTHS[m - 1]}`;
}

function longDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

function compactPrice(v, currency) {
  if (v == null) return '—';
  const abs = Math.abs(v);
  const digits = abs >= 1000 ? 0 : abs >= 100 ? 1 : 2;
  return fmtMoney(v, currency, digits);
}

export default function PriceChart({
  series = [],
  benchmark = null,
  currency = 'USD',
  type = 'line',
  showBenchmark = false,
  position = null,
  height = 300,
  compact = false,
  ariaLabel = 'Price chart',
}) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  const W = 640;
  const H = height;
  const L = 6;
  const R = compact ? 48 : 60;
  const T = 14;
  const B = 28;

  const model = useMemo(() => {
    const pts = (series || []).filter((p) => p && Number.isFinite(p.close));
    if (pts.length < 2) return null;
    const n = pts.length;
    const closes = pts.map((p) => p.close);
    const first = closes[0];

    let bench = null;
    if (showBenchmark && benchmark && Array.isArray(benchmark.closes)) {
      const raw = benchmark.closes;
      const b0 = raw.find((v) => v != null);
      if (b0) bench = raw.map((v) => (v == null ? null : (v / b0) * first));
    }

    const cost = position && Number(position.avgPrice) > 0 ? Number(position.avgPrice) : null;

    let lo = Math.min(...pts.map((p) => (type === 'candles' ? p.low : p.close)));
    let hi = Math.max(...pts.map((p) => (type === 'candles' ? p.high : p.close)));
    if (bench) {
      const bv = bench.filter((v) => v != null);
      lo = Math.min(lo, ...bv);
      hi = Math.max(hi, ...bv);
    }
    let costInRange = false;
    if (cost != null && cost > lo * 0.6 && cost < hi * 1.4) {
      lo = Math.min(lo, cost);
      hi = Math.max(hi, cost);
      costInRange = true;
    }
    const pad = (hi - lo || hi * 0.05 || 1) * 0.06;
    const yMin = lo - pad;
    const yMax = hi + pad;

    const x = (i) => L + (i / (n - 1)) * (W - L - R);
    const y = (v) => T + (1 - (v - yMin) / (yMax - yMin || 1)) * (H - T - B);

    const spanDays = (new Date(pts[n - 1].date) - new Date(pts[0].date)) / 86400000;
    const labelCount = compact ? 3 : 5;
    const xTicks = [];
    for (let k = 0; k < labelCount; k += 1) {
      const i = Math.round((k / (labelCount - 1)) * (n - 1));
      xTicks.push({ i, label: labelFor(pts[i].date, spanDays) });
    }

    return { pts, n, closes, first, bench, cost, costInRange, x, y, yTicks: niceTicks(yMin, yMax, compact ? 3 : 4), xTicks, step: (W - L - R) / (n - 1) };
  }, [series, benchmark, showBenchmark, position, type, H, L, R, T, B, compact]);

  if (!model) {
    return <div className="dd-price dd-price--empty" style={{ height }}><span className="dd-mono dd-muted">No price history</span></div>;
  }

  const { pts, n, closes, first, bench, cost, costInRange, x, y, yTicks, xTicks, step } = model;
  const last = closes[n - 1];
  const up = last >= first;

  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.close).toFixed(1)}`).join('');
  const areaPath = `${linePath}L${x(n - 1).toFixed(1)},${(H - B).toFixed(1)}L${x(0).toFixed(1)},${(H - B).toFixed(1)}Z`;
  const benchPath = bench
    ? bench.map((v, i) => (v == null ? '' : `${i === 0 || bench[i - 1] == null ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)).join('')
    : '';

  const onMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const px = ((clientX - rect.left) / rect.width) * W;
    const i = Math.max(0, Math.min(n - 1, Math.round(((px - L) / (W - L - R)) * (n - 1))));
    setHover(i);
  };
  const onLeave = () => setHover(null);

  const h = hover != null ? pts[hover] : null;
  const hx = hover != null ? x(hover) : 0;
  const tipLeft = hover != null ? (hx / W) * 100 : 0;
  const flipAt = compact ? 50 : 60;

  return (
    <div className={`dd-price ${compact ? 'dd-price--compact' : ''}`}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        role="img"
        aria-label={ariaLabel}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onTouchStart={onMove}
        onTouchMove={onMove}
        onTouchEnd={onLeave}
      >
        <defs>
          <linearGradient id="dd-price-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className="dd-price-stop-a" />
            <stop offset="100%" className="dd-price-stop-b" />
          </linearGradient>
        </defs>
        <g className="dd-price-grid">
          {yTicks.map((t) => <line key={`g${t}`} x1={L} x2={W - R} y1={y(t)} y2={y(t)} />)}
        </g>
        <g className="dd-price-ticks dd-mono">
          {yTicks.map((t) => <text key={`y${t}`} x={W - R + 8} y={y(t) + 4}>{compactPrice(t, currency)}</text>)}
          {xTicks.map((t) => <text key={`x${t.i}`} x={x(t.i)} y={H - 8} textAnchor={t.i === 0 ? 'start' : t.i === n - 1 ? 'end' : 'middle'}>{t.label}</text>)}
        </g>

        {type === 'candles' ? (
          <g className="dd-price-candles">
            {pts.map((p, i) => {
              const cx = x(i);
              const w = Math.max(1, Math.min(9, step * 0.62));
              const o = y(p.open);
              const c = y(p.close);
              const top = Math.min(o, c);
              const hgt = Math.max(1, Math.abs(o - c));
              return (
                <g key={p.date} className={p.close >= p.open ? 'up' : 'down'}>
                  <line x1={cx} x2={cx} y1={y(p.high)} y2={y(p.low)} />
                  <rect x={cx - w / 2} y={top} width={w} height={hgt} />
                </g>
              );
            })}
          </g>
        ) : (
          <>
            <path className="dd-price-area" d={areaPath} />
            <path className={`dd-price-line ${up ? 'up' : 'down'}`} d={linePath} />
          </>
        )}

        {bench && <path className="dd-price-bench" d={benchPath} />}
        {bench && <text className="dd-price-bench-label dd-mono" x={W - R - 4} y={y(bench[n - 1] ?? first) - 6} textAnchor="end">S&amp;P 500, rebased</text>}

        {cost != null && costInRange && (
          <g className="dd-price-cost">
            <line x1={L} x2={W - R} y1={y(cost)} y2={y(cost)} />
            <text className="dd-mono" x={L + 4} y={y(cost) - 5}>your cost {compactPrice(cost, currency)}</text>
          </g>
        )}
        {cost != null && !costInRange && (
          <text className="dd-price-cost-off dd-mono" x={L + 4} y={T + 10}>your cost {compactPrice(cost, currency)} is {cost < first ? 'below' : 'above'} this range</text>
        )}

        {h && (
          <g className="dd-price-hover">
            <line x1={hx} x2={hx} y1={T} y2={H - B} />
            <circle cx={hx} cy={y(h.close)} r={3.5} />
            {bench && bench[hover] != null && <circle className="bench" cx={hx} cy={y(bench[hover])} r={3} />}
          </g>
        )}
      </svg>

      {h && (
        <div className={`dd-price-tip dd-mono ${tipLeft > flipAt ? 'left' : ''}`} style={{ left: `${tipLeft}%` }} role="status">
          <div className="dd-price-tip-date">{longDate(h.date)}</div>
          {type === 'candles' ? (
            <div className="dd-price-tip-row">
              <span>O {compactPrice(h.open, currency)}</span><span>H {compactPrice(h.high, currency)}</span>
              <span>L {compactPrice(h.low, currency)}</span><span>C {compactPrice(h.close, currency)}</span>
            </div>
          ) : (
            <div className="dd-price-tip-row"><span>{compactPrice(h.close, currency)}</span><span className={signClass(h.close - first)}>{fmtPct(((h.close - first) / first) * 100)} since start</span></div>
          )}
          {bench && bench[hover] != null && (
            <div className="dd-price-tip-row dd-muted"><span>S&amp;P 500</span><span className={signClass(bench[hover] - first)}>{fmtPct(((bench[hover] - first) / first) * 100)}</span></div>
          )}
          {cost != null && (
            <div className="dd-price-tip-row dd-muted"><span>vs your cost</span><span className={signClass(h.close - cost)}>{fmtPct(((h.close - cost) / cost) * 100)}</span></div>
          )}
        </div>
      )}
    </div>
  );
}
